import React, { useEffect, useRef } from 'react';
import { cn } from 'lib/utils';
import { usePrefersReducedMotion } from 'hooks/use-media-query';
import { BLOB_COUNT, blobPose, orbLevelFor, smoothLevel, type OrbState } from './voiceOrbMotion';

interface Props {
  state: OrbState;
  /** The measured voice level, 0-1. The candidate's follows it; the examiner's moves like speech without it. */
  level?: number;
  size?: number;
  className?: string;
}

type Rgb = readonly [number, number, number];

interface Look {
  /** Three overlapping blobs: the bright body, a cooler layer, and a deeper one that gives it depth on white. */
  blobs: readonly [Rgb, Rgb, Rgb];
  /** Colour of the soft light behind the orb. */
  glow: Rgb;
  /** The thin outline that swells with the voice; darker than the blobs, since volt barely shows on white. */
  ring: string;
}

const VOLT_BLOBS: Look['blobs'] = [
  [209, 254, 23],
  [45, 212, 191],
  [101, 163, 13],
];

/** Volt for the examiner, sky blue for the candidate's turn, quiet volt while waiting. */
const LOOK: Record<OrbState, Look> = {
  examiner: { blobs: VOLT_BLOBS, glow: [190, 235, 20], ring: 'rgba(77, 124, 15, 0.7)' },
  candidate: {
    blobs: [
      [56, 189, 248],
      [129, 140, 248],
      [14, 116, 200],
    ],
    glow: [56, 189, 248],
    ring: 'rgba(14, 116, 200, 0.65)',
  },
  idle: { blobs: VOLT_BLOBS, glow: [190, 235, 20], ring: 'rgba(77, 124, 15, 0.4)' },
  thinking: { blobs: VOLT_BLOBS, glow: [190, 235, 20], ring: 'rgba(77, 124, 15, 0.5)' },
};

const BODY_ALPHA = [0.95, 0.8, 0.7] as const;

/**
 * Each blob is a different ellipse (width and height as a percentage of the body).
 * A circle turning is invisible; ellipses of different proportions turning and
 * overlapping is what makes the silhouette change.
 */
const BLOB_SHAPES = [
  { w: 100, h: 72 },
  { w: 74, h: 100 },
  { w: 92, h: 64 },
] as const;

const rgba = ([r, g, b]: Rgb, alpha: number) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

const blobFill = (color: Rgb, alpha: number) =>
  `radial-gradient(ellipse at 42% 38%, ${rgba(color, alpha)} 0%, ${rgba(color, alpha * 0.85)} 52%, ${rgba(color, 0)} 78%)`;

const poseTransform = (index: number, seconds: number, voice: number, size: number) => {
  const pose = blobPose(index, seconds, voice);
  return `translate(${pose.x * size}px, ${pose.y * size}px) rotate(${pose.rotate}deg) scale(${pose.scaleX}, ${pose.scaleY})`;
};

/**
 * A soft, shape-shifting blob that moves with a voice, like a voice assistant's.
 * Decorative: what it shows is also said in words next to it. Only transform
 * and opacity change, written straight to the elements once per frame so the
 * page does not re-render sixty times a second; with reduced motion it stays still.
 */
export function VoiceOrb({ state, level, size = 168, className }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const stateRef = useRef(state);
  const levelRef = useRef(level);
  stateRef.current = state;
  levelRef.current = level;
  const glow = useRef<HTMLDivElement>(null);
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const blobs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (reducedMotion) {
      // Still, but already an organic shape rather than stacked circles.
      blobs.current.forEach((el, i) => {
        if (el) el.style.transform = poseTransform(i, 1.5 + i, 0.15, size);
      });
      return undefined;
    }
    const startedAt = performance.now();
    let voice = 0;
    let lagging = 0;
    let frame = 0;
    const tick = (now: number) => {
      const seconds = (now - startedAt) / 1000;
      const waiting = stateRef.current === 'idle' || stateRef.current === 'thinking';
      const target = orbLevelFor(stateRef.current, levelRef.current, seconds);
      voice = smoothLevel(voice, target);
      // The outer rings and the light behind trail the voice, which reads as a ripple moving outwards.
      lagging = smoothLevel(lagging, target, 0.18, 0.05);
      const breath = 0.5 + 0.5 * Math.sin(seconds * Math.PI * 2 * (stateRef.current === 'thinking' ? 0.9 : 0.35));
      const rest = stateRef.current === 'thinking' ? 0.07 : waiting ? 0.035 : 0.015;
      // Waiting blobs still turn, only gently, so the orb never looks frozen.
      const felt = waiting ? 0.08 + breath * 0.06 : voice;
      for (let i = 0; i < BLOB_COUNT; i += 1) {
        const el = blobs.current[i];
        if (!el) continue;
        el.style.transform = poseTransform(i, seconds, felt, size);
      }
      if (body.current) body.current.style.transform = `scale(${1 + voice * 0.16 + breath * rest})`;
      if (inner.current) {
        inner.current.style.transform = `scale(${1 + lagging * 0.3 + breath * rest})`;
        inner.current.style.opacity = String(0.2 + voice * 0.55);
      }
      if (outer.current) {
        outer.current.style.transform = `scale(${1 + lagging * 0.55 + breath * rest * 1.5})`;
        outer.current.style.opacity = String(0.1 + lagging * 0.45);
      }
      if (glow.current) {
        glow.current.style.transform = `scale(${1.15 + lagging * 0.5})`;
        glow.current.style.opacity = String(0.45 + voice * 0.55);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion, size]);

  const look = LOOK[state];
  return (
    <div
      aria-hidden
      data-state={state}
      className={cn('pointer-events-none relative isolate shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <div
        ref={glow}
        className="absolute inset-0 -z-10 rounded-full blur-2xl will-change-transform"
        style={{ background: `radial-gradient(circle, ${rgba(look.glow, 0.5)} 0%, ${rgba(look.glow, 0)} 68%)`, opacity: 0.5, transform: 'scale(1.15)' }}
      />
      <div ref={outer} className="absolute inset-0 rounded-full border border-[#0A1628]/15 will-change-transform" style={{ opacity: 0.15 }} />
      <div
        ref={inner}
        className="absolute inset-0 rounded-full border-[1.5px] will-change-transform"
        style={{ borderColor: look.ring, opacity: 0.3 }}
      />
      <div ref={body} className="absolute inset-[9%] will-change-transform">
        {look.blobs.map((color, i) => (
          <div
            key={i}
            ref={(el) => {
              blobs.current[i] = el;
            }}
            className="absolute rounded-full will-change-transform"
            style={{
              width: `${BLOB_SHAPES[i].w}%`,
              height: `${BLOB_SHAPES[i].h}%`,
              left: `${(100 - BLOB_SHAPES[i].w) / 2}%`,
              top: `${(100 - BLOB_SHAPES[i].h) / 2}%`,
              background: blobFill(color, BODY_ALPHA[i]),
              filter: `blur(${Math.max(3, Math.round(size / 50))}px)`,
            }}
          />
        ))}
      </div>
      {state === 'thinking' && !reducedMotion && (
        <div className="absolute inset-[3%] animate-spin rounded-full border-2 border-transparent border-t-[#4D7C0F]/80 [animation-duration:1.6s]" />
      )}
    </div>
  );
}
